// Client for the self-improvement ("Learning") API, run feedback, chain version history,
// and the platform-admin controls. Backend: api/core/routers/self_improvement.py & friends.

export type TokenGetter = () => Promise<string | null | undefined>;
export type TargetType = 'function' | 'chain';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function request<T>(getToken: TokenGetter, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
    } catch {
      /* keep statusText */
    }
    throw new ApiError(message, res.status);
  }
  return res.json();
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LearningSettings {
  knowledge: {
    enabled: boolean; auto_publish: boolean; min_evidence: number; min_confidence: number; inject: boolean; top_k: number; min_similarity: number;
  };
  suggestions: { enabled: boolean };
  insights: { enabled: boolean };
  signals: { enabled: string[]; known?: string[]; gcs_review_prefix?: string };
  sampling: {
    window_days: number; sample_percent: number; min_runs: number; max_runs: number; max_chars_per_run: number;
    success_only: boolean; only_with_signals: boolean; rescan_on_signal_upgrade: boolean;
  };
  budget: { credit_cap: number | null };
  schedule: { mode: 'manual' | 'interval' | 'criteria'; interval_days: number; min_new_runs: number; min_new_signals: number; min_hours_between: number };
  scope: { learn_nodes: string[]; inject_nodes: string[] };
  models: { profile: string | null; extract: string | null; review: string | null };
}

export interface LearningStatus {
  enabled: boolean;
  retention_days?: number | null;
  blocked_reason?: string | null;
  paused?: boolean;
  allow_model_override?: boolean;
  models?: Record<string, string>;
  defaults?: LearningSettings;
  signal_providers?: string[];
  limits?: { min_runs: number; max_runs: number; min_credit_cap: number; max_credit_cap: number };
}

export interface LearningConfigSummary {
  id: string;
  target_type: TargetType;
  target_id: string;
  target_name: string | null;
  enabled: boolean;
  profile_status: 'none' | 'generated' | 'edited';
  profile_updated_at: string | null;
  knowledge_category_id: string | null;
  knowledge_published_at: string | null;
  last_cycle_at: string | null;
  last_cycle_status: string | null;
  entry_counts: Record<string, number>;
  open_suggestions: number;
  updated_at: string | null;
}

export interface TargetNode { node_id: string; label: string; kind: 'ai_function' | 'plugin'; fields?: string[] }

export interface LearningProfile {
  summary: string;
  success_criteria: string[];
  failure_modes: string[];
  observation: { key_label: string; item_label: string; key_examples: string[]; generalize: string; categories: string[] };
  fact_topics: string[];
  learn_nodes: string[];
  inject: { node_id: string | null; query_template: string; why?: string }[];
  notes: string;
}

export interface CycleSummary {
  id: string;
  trigger: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'skipped';
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  credits_used: number | null;
  credit_cap: number | null;
  tokens_used: number | null;
  error: string | null;
  created_by: string | null;
  summary: {
    planned?: number; analyzed?: number; quality?: Record<string, number>;
    knowledge?: Record<string, number>; suggestions?: Record<string, number>; stopped_early?: string | null;
  };
}

export interface ScannedItem {
  source_type: string;
  source_id: string;
  source_created_at: string | null;
  signal_tier: number;
  signal_score: number;
  signals: { provider: string; label: string; score: number; explicit: boolean; note?: string }[];
  ai_quality: 'good' | 'mixed' | 'bad' | null;
  ai_reason: string | null;
  history_available: boolean;
  link: string | null;
}

export interface CycleReport {
  summary?: string;
  what_works?: string[];
  what_fails?: { issue: string; frequency?: string; example_refs?: string[] }[];
  gaps?: { topic: string; examples?: string[] }[];
  other?: string[];
}

export interface CycleDetail extends CycleSummary {
  window_start: string | null;
  window_end: string | null;
  plan: Plan | null;
  stats: Record<string, any> | null;
  report: CycleReport | null;
  models: Record<string, string> | null;
  log: { at: string; level: string; msg: string }[];
  scanned: { total: number; items: ScannedItem[] };
}

export interface StageEstimate { model: string; calls: number; input_tokens: number; output_tokens: number; credits: number }

export interface Plan {
  window_start: string;
  window_end: string;
  total_in_window: number;
  already_scanned: number;
  rescan_eligible: number;
  unscanned: number;
  duplicates_skipped: number;
  filtered_out: number;
  eligible: number;
  planned: number;
  sample_percent_of_eligible: number;
  tier_counts_eligible: Record<string, number>;
  tier_counts_planned: Record<string, number>;
  /** Test cases among the eligible / planned runs (source "test_results"). */
  tests_eligible?: { failed: number; passed: number };
  tests_planned?: { failed: number; passed: number };
  est_chars_per_run: number;
  estimate: { extract?: StageEstimate; review?: StageEstimate; profile?: StageEstimate; total_credits: number };
  min_cycle_credits: number;
  recommended_cap: number;
  credit_cap: number;
  cap_is_auto: boolean;
  cap_limited_runs: number | null;
  can_run: boolean;
  blockers: string[];
  warnings: string[];
  cap_error?: string | null;
}

export interface MappingItem {
  label: string;
  label_norm?: string;
  details?: Record<string, any>;
  positive: number;
  negative: number;
  neutral: number;
  notes?: string[];
  last_seen?: string;
}

export interface EntryOrigin {
  target_type: TargetType;
  target_id: string;
  target_name: string;
  entry_id: string;
  /** Supporting runs it had in the source when copied. */
  evidence_count: number;
  imported_at: string;
  imported_by: string | null;
}

export interface ImportResult {
  source_name: string;
  to_import: number;
  imported: number;
  already_here: number;
  /** Source entry ids skipped because this target already has an entry with that name. */
  already_here_ids: string[];
  status: 'candidate' | 'active';
  /** More entries matched than one import takes (500). */
  capped: boolean;
}

export interface LearnedEntry {
  id: string;
  kind: 'mapping' | 'fact';
  key: string;
  aliases: string[];
  category: string | null;
  content: { items?: MappingItem[]; text?: string };
  evidence_positive: number;
  evidence_negative: number;
  evidence_count: number;
  confidence: number;
  status: 'candidate' | 'active' | 'pinned' | 'rejected' | 'stale';
  human_edited: boolean;
  /** Set when the entry was imported from another function or chain. */
  origin?: EntryOrigin | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  updated_at: string | null;
  evidence_refs_count: number;
  history_available?: number;
  orphaned?: boolean;
  /** Exactly what a matching run receives for this entry. */
  sent_text?: string;
  in_use?: boolean;
}

export interface EvidenceRef {
  type: string;
  id: string;
  at: string | null;
  polarity: string;
  history_available: boolean;
  link: string | null;
  /** What this run contributed. Missing on refs recorded before Oct 2026. */
  item?: string | null;
  text?: string | null;
  note?: string | null;
  run_quality?: 'good' | 'mixed' | 'bad' | null;
  run_quality_reason?: string | null;
  input_preview?: string | null;
}

export interface Suggestion {
  id: string;
  cycle_id: string | null;
  kind: 'instructions' | 'parameter' | 'model' | 'node_config' | 'structure' | 'knowledge_gap' | 'other';
  title: string;
  rationale: string | null;
  target: { node_id: string | null; function_id: string | null } | null;
  proposed_change: { field: string | null; current: any; proposed: any } | null;
  evidence: { runs: { type: string; id: string; at: string | null }[] } | null;
  priority: 'high' | 'medium' | 'low';
  confidence: number | null;
  status: 'open' | 'accepted' | 'dismissed' | 'superseded' | 'reverted' | 'failed';
  applied_result: Record<string, any> | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string | null;
  auto_applicable: boolean;
  /** Open, auto-applicable suggestions only: how it relates to the live version. */
  freshness: SuggestionFreshness | null;
}

/**
 * current:  written against the live version.
 * rebased:  written against an older version; its edits touch other lines, so they carry over (`rebased`).
 * conflict: edits the same lines as changes made since; only "Combine & apply" can use it.
 * applied:  the live version already has it.
 * changed:  a setting whose value was edited since (`live`).
 * blocked:  can't be applied (`message`).
 */
export interface SuggestionFreshness {
  state: 'current' | 'rebased' | 'conflict' | 'applied' | 'changed' | 'blocked';
  live?: any;
  live_function_id?: string;
  live_version?: number;
  rebased?: string;
  conflict_line?: string;
  /** Lines the rewrite deletes outright (not reworded or moved). */
  dropped_lines?: string[];
  /** Other open suggestions editing the same lines: applying one makes the other conflict. */
  overlaps?: string[];
  message?: string;
}

export interface CombineGroup {
  function_id: string;
  function_name: string;
  version: number;
  current: string;
  combined: string;
  method: 'merged' | 'compiled' | null;
  model: string | null;
  suggestion_ids: string[];
  already_included: string[];
  notes: string[];
  skipped: { id: string; title: string; reason: string }[];
  dropped_lines: string[];
}

export interface CombinePreview {
  groups: CombineGroup[];
  settings: { suggestion_id: string; title: string; node_id: string | null; field: string; from: any; to: any }[];
  already_included: string[];
  errors: string[];
  credits_used: number;
}

export interface BatchResult {
  batch_id: string;
  versions: { function_name: string; new_version: number; previous_version: number }[];
  applied: number;
  already_included: number;
}

export interface TargetDetail {
  target: { type: TargetType; id: string; name: string; nodes?: TargetNode[]; latest_function_id?: string; version?: number; versions?: number };
  config: LearningConfigSummary | null;
  settings: LearningSettings;
  warnings: string[];
  profile: LearningProfile | null;
  knowledge: { category_id: string; category_name: string | null; material_id: string | null; chunks: number; published_at: string | null } | null;
  active_cycle: CycleSummary | null;
  recent_changes?: AppliedChange[];
}

/** Aggregates over a set of runs. Feedback is attributed to the run it's about (by run date). */
export interface OutcomeMetrics {
  runs: number;
  failed: number;
  success_rate: number | null;
  avg_ms: number | null;
  with_learned: number;
  feedback: number;
  rated_runs: number;
  coverage: number | null;
  avg_score: number | null;
  positive: number;
  negative: number;
  pct_positive: number | null;
}

export interface OutcomeVerdict {
  direction: 'better' | 'worse' | 'same' | 'unknown';
  basis: 'ratings' | 'success_rate' | null;
  delta_pct_positive?: number;
  delta_avg_score?: number;
  delta_success_rate?: number;
}

export interface ChangeComparison {
  id: string;
  title: string;
  kind: string;
  decided_at: string;
  manual: boolean;
  new_version: number | null;
  window_days: number;
  before: OutcomeMetrics | null;
  after: OutcomeMetrics | null;
  verdict: OutcomeVerdict;
}

export interface VersionOutcome extends OutcomeMetrics {
  version: number;
  function_id: string;
  created_at: string | null;
  created_by: string | null;
  is_current: boolean;
  first_day: string | null;
  last_day: string | null;
  from_suggestion: { id: string; title: string } | null;
}

export interface DailyOutcome {
  date: string;
  runs: number;
  failed: number;
  with_learned: number;
  feedback: number;
  positive: number;
  negative: number;
  neutral: number;
  avg_score: number | null;
  pct_positive: number | null;
  success_rate: number | null;
  /** Function runs per version ("3": 120); empty for chains. */
  by_version: Record<string, number>;
}

export interface TargetResults {
  days: number;
  since: string;
  until: string;
  totals: OutcomeMetrics;
  previous: OutcomeMetrics;
  daily: DailyOutcome[];
  versions: VersionOutcome[];
  changes: ChangeComparison[];
  knowledge: { published: boolean; with: OutcomeMetrics; without: OutcomeMetrics };
  markers: { date: string; at: string; kind: 'change' | 'version' | 'analysis'; label: string }[];
  labels: { label: string; count: number; avg_score: number }[];
  thresholds: { min_ratings: number; min_runs: number; compare_days: number };
}

export interface AppliedChange {
  id: string;
  title: string;
  kind: string;
  decided_at: string | null;
  decided_by: string | null;
  manual: boolean;
  applied_result: Record<string, any>;
  runs_since: number;
}

export interface KnowledgeStepUsage {
  node_id: string;
  label: string;
  kind: 'ai_function' | 'plugin';
  gets_learned: boolean;
  categories: { id: string; name: string | null }[];
  knowledge_query: string | null;
}

export interface KnowledgeDetails {
  category: { id: string; name: string } | null;
  material: { id: string; name: string; file_name: string; gcs_path: string | null } | null;
  published_at: string | null;
  live_chunks: number;
  counts: { live: number; waiting: number; rejected: number; stale: number };
  thresholds: { auto_publish: boolean; min_evidence: number; min_confidence: number; min_similarity: number };
  usage: {
    in_use: boolean;
    blockers: string[];
    top_k: number;
    steps: KnowledgeStepUsage[] | null;
    steps_source?: 'settings' | 'profile' | 'all';
  };
  document: string | null;
}

export interface KnowledgeSearchResult {
  text: string;
  entry_id: string | null;
  key: string | null;
  kind: string | null;
  /** exact = the entry's name/alias appears in the query; similar = vector match */
  match: 'exact' | 'similar';
  matched: string | null;
  similarity: number | null;
  used: boolean;
  reason: 'below_cutoff' | 'over_limit' | null;
}

export interface AttentionItem {
  target_type: TargetType;
  target_id: string;
  target_name?: string | null;
  open_suggestions: number;
  waiting_entries: number;
  applied_changes?: number;
  last_applied_at?: string | null;
  last_applied_title?: string | null;
}

export interface MaterialChunk { index: number; content: string; chars: number; embedding_model: string | null; metadata: Record<string, any> }

/** The customer's Knowledge Base categories (vectors router; customer comes from the token). */
export async function fetchKnowledgeCategories(getToken: TokenGetter): Promise<{ id: string; name: string }[]> {
  const res = await request<{ data: { items: { id: string; name: string }[] } }>(getToken, '/api/v2/customers/current/categories');
  return res.data.items;
}

/** Stored snippets of any Knowledge Base material (vectors router; customer comes from the token). */
export async function fetchMaterialChunks(
  getToken: TokenGetter, materialId: string, opts: { limit?: number; offset?: number; q?: string } = {},
): Promise<{ total: number; items: MaterialChunk[] }> {
  const params = new URLSearchParams({ limit: String(opts.limit ?? 50), offset: String(opts.offset ?? 0) });
  if (opts.q) params.set('q', opts.q);
  const res = await request<{ data: { total: number; items: MaterialChunk[] } }>(
    getToken, `/api/v2/customers/current/materials/${materialId}/chunks?${params}`,
  );
  return res.data;
}

/** What a single run or chain step was given as context (stored by the executors). */
export interface KnowledgeTrace {
  calls: number;
  calls_with_knowledge: number;
  learned: number;
  category: number;
  categories: string[];
  snippets: { source: 'learned' | 'category'; text: string }[];
}

export interface FeedbackRecord {
  id: string;
  source_type: string;
  source_id: string;
  target_type: string | null;
  target_id: string | null;
  label: string;
  score: number;
  note: string | null;
  item_ref: string | null;
  metadata?: Record<string, any> | null;
  created_by: string | null;
  via_api?: boolean;
  created_at: string | null;
}

export interface Snapshot {
  id: string;
  name: string | null;
  description: string | null;
  reason: 'auto_save' | 'manual' | 'before_restore' | 'before_suggestion' | 'restore';
  pinned: boolean;
  learning_suggestion_id: string | null;
  created_by: string | null;
  created_at: string | null;
  node_count: number;
  is_current: boolean;
  changes?: { added: string[]; removed: string[]; changed: string[] } | null;
}

export interface FeatureFlagInfo {
  key: string;
  description: string | null;
  enabled_for_all: boolean;
  customers: { id: string; name: string | null; exists: boolean }[];
  updated_at: string | null;
  updated_by: string | null;
}

export interface SystemSettings {
  kill_switch: boolean;
  models: { profile: string; extract: string; review: string; compile: string };
  allow_customer_model_override: boolean;
  max_concurrent_cycles: number;
  runs_per_extract_call: number;
  max_candidates_scanned: number;
  default_credit_cap: number;
}

// ---------------------------------------------------------------------------
// Learning API
// ---------------------------------------------------------------------------

const L = '/api/v2/learning';
const t = (type: TargetType, id: string) => `${L}/targets/${type}/${encodeURIComponent(id)}`;

export const learningApi = (getToken: TokenGetter) => ({
  status: () => request<LearningStatus>(getToken, `${L}/status`),
  configs: () => request<{ configs: LearningConfigSummary[] }>(getToken, `${L}/configs`),
  target: (type: TargetType, id: string) => request<TargetDetail>(getToken, t(type, id)),
  updateTarget: (type: TargetType, id: string, body: { enabled?: boolean; settings?: LearningSettings }) =>
    request<{ config: LearningConfigSummary; settings: LearningSettings; warnings: string[] }>(getToken, t(type, id), { method: 'PUT', body: JSON.stringify(body) }),
  preview: (type: TargetType, id: string, settings?: LearningSettings) => request<Plan>(getToken, `${t(type, id)}/preview`, json({ settings: settings ?? null })),

  generateProfile: (type: TargetType, id: string, hints?: string, refine = false) =>
    request<{ profile: LearningProfile; profile_status: string; credits_used: number }>(
      getToken, `${t(type, id)}/profile/generate`, json({ hints: hints || null, refine }),
    ),
  saveProfile: (type: TargetType, id: string, profile: LearningProfile) =>
    request<{ profile: LearningProfile; profile_status: string }>(getToken, `${t(type, id)}/profile`, { method: 'PUT', body: JSON.stringify({ profile }) }),

  startCycle: (type: TargetType, id: string) => request<{ cycle: CycleSummary; plan: Plan }>(getToken, `${t(type, id)}/cycles`, { method: 'POST' }),
  cycles: (type: TargetType, id: string, limit = 25, offset = 0) =>
    request<{ total: number; cycles: CycleSummary[] }>(getToken, `${t(type, id)}/cycles?limit=${limit}&offset=${offset}`),
  cycle: (cycleId: string, itemsLimit = 100, itemsOffset = 0) =>
    request<CycleDetail>(getToken, `${L}/cycles/${cycleId}?items_limit=${itemsLimit}&items_offset=${itemsOffset}`),
  cancelCycle: (cycleId: string) => request<{ cycle: CycleSummary }>(getToken, `${L}/cycles/${cycleId}/cancel`, { method: 'POST' }),

  entries: (type: TargetType, id: string, params: { status?: string; kind?: string; q?: string; orphaned?: boolean; sort?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    return request<{ total: number; entries: LearnedEntry[] }>(getToken, `${t(type, id)}/entries?${qs.toString()}`);
  },
  updateEntry: (entryId: string, body: Partial<Pick<LearnedEntry, 'status' | 'key' | 'category' | 'content'>>) =>
    request<{ entry: LearnedEntry }>(getToken, `${L}/entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEntry: (entryId: string) => request<{ deleted: number }>(getToken, `${L}/entries/${entryId}`, { method: 'DELETE' }),
  bulkDeleteEntries: (
    type: TargetType,
    id: string,
    body: { ids?: string[]; orphaned?: boolean; status?: string; all?: boolean; rescan?: boolean },
  ) => request<{ deleted: number; runs_forgotten?: number }>(getToken, `${t(type, id)}/entries/bulk-delete`, json(body)),
  bulkStatus: (type: TargetType, id: string, ids: string[], status: LearnedEntry['status']) =>
    request<{ updated: number }>(getToken, `${t(type, id)}/entries/bulk-status`, json({ ids, status })),
  evidence: (entryId: string) => request<{ entry_id: string; refs: EvidenceRef[] }>(getToken, `${L}/entries/${entryId}/evidence`),
  publish: (type: TargetType, id: string) =>
    request<{ published_entries: number; waiting_entries: number }>(getToken, `${t(type, id)}/knowledge/publish`, { method: 'POST' }),
  knowledge: (type: TargetType, id: string) => request<KnowledgeDetails>(getToken, `${t(type, id)}/knowledge`),
  searchKnowledge: (type: TargetType, id: string, query: string, topK?: number) =>
    request<{ top_k: number; min_similarity: number; results: KnowledgeSearchResult[] }>(
      getToken, `${t(type, id)}/knowledge/search`, json({ query, top_k: topK ?? null }),
    ),
  exportKnowledge: (
    type: TargetType, id: string,
    body: { entry_ids?: string[] | null; category_id?: string | null; new_category_name?: string | null; name?: string | null },
  ) =>
    request<{ material_id: string; material_name: string; category_id: string; category_name: string; entries: number; snippets: number }>(
      getToken, `${t(type, id)}/knowledge/export`, json(body),
    ),
  importKnowledge: (
    type: TargetType, id: string,
    body: { source_type: TargetType; source_id: string; entry_ids?: string[] | null; status: 'candidate' | 'active'; dry_run?: boolean },
  ) => request<ImportResult>(getToken, `${t(type, id)}/knowledge/import`, json(body)),
  renameCategory: (type: TargetType, id: string, name: string) =>
    request<{ id: string; name: string }>(getToken, `${t(type, id)}/knowledge/category`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  attention: () => request<{ items: AttentionItem[] }>(getToken, `${L}/attention`),

  suggestions: (type: TargetType, id: string, status = 'open') =>
    request<{ suggestions: Suggestion[] }>(getToken, `${t(type, id)}/suggestions?status=${status}`),
  /** `baseFunctionId`: the version an edited instructions value was written against. */
  acceptSuggestion: (suggestionId: string, value?: any, baseFunctionId?: string) =>
    request<{ suggestion: Suggestion; result: Record<string, any> }>(
      getToken, `${L}/suggestions/${suggestionId}/accept`, json({ value: value ?? null, base_function_id: baseFunctionId ?? null }),
    ),
  combineSuggestions: (type: TargetType, id: string, ids: string[], useAi = false) =>
    request<CombinePreview>(getToken, `${t(type, id)}/suggestions/combine`, json({ ids, use_ai: useAi })),
  applySuggestionsBatch: (type: TargetType, id: string, ids: string[], instructions: { function_id: string; text: string }[]) =>
    request<BatchResult>(getToken, `${t(type, id)}/suggestions/apply-batch`, json({ ids, instructions })),
  dismissSuggestion: (suggestionId: string) => request<{ suggestion: Suggestion }>(getToken, `${L}/suggestions/${suggestionId}/dismiss`, { method: 'POST' }),
  reopenSuggestion: (suggestionId: string) => request<{ suggestion: Suggestion }>(getToken, `${L}/suggestions/${suggestionId}/reopen`, { method: 'POST' }),
  undoSuggestion: (suggestionId: string) =>
    request<{ suggestion: Suggestion; result: Record<string, any> }>(getToken, `${L}/suggestions/${suggestionId}/undo`, { method: 'POST' }),

  targetFeedback: (type: TargetType, id: string) =>
    request<{ counts: Record<string, number>; recent: (FeedbackRecord & { link: string })[] }>(getToken, `${t(type, id)}/feedback`),
  results: (type: TargetType, id: string, days = 30) => request<TargetResults>(getToken, `${t(type, id)}/results?days=${days}`),
});

// ---------------------------------------------------------------------------
// Feedback, snapshots, admin
// ---------------------------------------------------------------------------

export const feedbackApi = (getToken: TokenGetter) => ({
  list: (sourceType: string, sourceId: string, customerId?: string | null) =>
    request<{ feedback: FeedbackRecord[] }>(
      getToken,
      `/api/v2/feedback?source_type=${sourceType}&source_id=${sourceId}${customerId ? `&customer_id=${encodeURIComponent(customerId)}` : ''}`,
    ),
  create: (body: { source_type: string; source_id: string; label: string; note?: string; score?: number; item_ref?: string }) =>
    request<FeedbackRecord>(getToken, '/api/v2/feedback', json(body)),
  remove: (id: string) => request<{ deleted: number }>(getToken, `/api/v2/feedback/${id}`, { method: 'DELETE' }),
});

export const snapshotsApi = (getToken: TokenGetter) => ({
  list: (chainId: string) => request<{ snapshots: Snapshot[] }>(getToken, `/api/v2/chains/${chainId}/snapshots`),
  get: (chainId: string, snapshotId: string) =>
    request<Snapshot & { graph_data: any; config: any; changes_vs_current: { added: string[]; removed: string[]; changed: string[] }; missing_functions: string[] }>(
      getToken, `/api/v2/chains/${chainId}/snapshots/${snapshotId}`,
    ),
  save: (chainId: string, name: string, description?: string) => request<Snapshot>(getToken, `/api/v2/chains/${chainId}/snapshots`, json({ name, description })),
  restore: (chainId: string, snapshotId: string) =>
    request<{ restored_from: string; undo_snapshot_id: string; warnings: string[]; graph_data: any; config: any }>(
      getToken, `/api/v2/chains/${chainId}/snapshots/${snapshotId}/restore`, { method: 'POST' },
    ),
  update: (chainId: string, snapshotId: string, body: { name?: string; description?: string; pinned?: boolean }) =>
    request<Snapshot>(getToken, `/api/v2/chains/${chainId}/snapshots/${snapshotId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (chainId: string, snapshotId: string) => request<{ deleted: number }>(getToken, `/api/v2/chains/${chainId}/snapshots/${snapshotId}`, { method: 'DELETE' }),
});

export const learningAdminApi = (getToken: TokenGetter) => ({
  flags: () => request<{ flags: FeatureFlagInfo[] }>(getToken, '/api/v2/admin/feature-flags'),
  updateFlag: (key: string, body: { enabled_for_all?: boolean; customer_ids?: string[] }) =>
    request<FeatureFlagInfo>(getToken, `/api/v2/admin/feature-flags/${key}`, { method: 'PUT', body: JSON.stringify(body) }),
  settings: () =>
    request<{ settings: SystemSettings; analyzer_functions: { name: string; function_id: string; stage: string; description: string; allowed_models: string[] }[] }>(
      getToken, '/api/v2/admin/learning/settings',
    ),
  saveSettings: (settings: SystemSettings) =>
    request<{ settings: SystemSettings }>(getToken, '/api/v2/admin/learning/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  overview: (days = 30) => request<{
    since: string;
    cycle_status_counts: Record<string, number>;
    total_credits: number;
    configs: {
      id: string; customer_id: string; customer_name: string | null; target_type: TargetType; target_id: string; target_name: string | null;
      enabled: boolean; schedule: string; last_cycle_at: string | null; last_cycle_status: string | null; cycles: number; credits: number;
      open_suggestions: number; waiting_entries: number;
    }[];
  }>(getToken, `/api/v2/admin/learning/overview?days=${days}`),
  searchCustomers: (search: string) =>
    request<{ data: { id: string; name: string; customer_tag: string }[] }>(
      getToken, `/api/v2/admin/customers?page_size=10&search=${encodeURIComponent(search)}`,
    ),
});

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

let statusCache: { at: number; promise: Promise<LearningStatus> } | null = null;

/** Cached for a minute so many buttons on one page share a single request. */
export function getLearningStatus(getToken: TokenGetter, force = false): Promise<LearningStatus> {
  if (!force && statusCache && Date.now() - statusCache.at < 60_000) return statusCache.promise;
  const promise = learningApi(getToken).status().catch(() => ({ enabled: false } as LearningStatus));
  statusCache = { at: Date.now(), promise };
  return promise;
}

let attentionCache: { at: number; promise: Promise<AttentionItem[]> } | null = null;

/** Per-target badge counts, shared by list pages and cached for a minute. */
export function getLearningAttention(getToken: TokenGetter, force = false): Promise<AttentionItem[]> {
  if (!force && attentionCache && Date.now() - attentionCache.at < 60_000) return attentionCache.promise;
  const promise = learningApi(getToken).attention().then((r) => r.items).catch(() => [] as AttentionItem[]);
  attentionCache = { at: Date.now(), promise };
  return promise;
}

export function invalidateLearningAttention(): void {
  attentionCache = null;
}

let configsCache: { at: number; promise: Promise<LearningConfigSummary[]> } | null = null;

/** Which targets have self-improvement set up (and on), shared by list pages and cached for a minute. */
export function getLearningConfigs(getToken: TokenGetter, force = false): Promise<LearningConfigSummary[]> {
  if (!force && configsCache && Date.now() - configsCache.at < 60_000) return configsCache.promise;
  const promise = learningApi(getToken).configs().then((r) => r.configs).catch(() => [] as LearningConfigSummary[]);
  configsCache = { at: Date.now(), promise };
  return promise;
}

export function invalidateLearningConfigs(): void {
  configsCache = null;
}

export function learningPath(type: TargetType, id: string, tab?: string): string {
  return `/dashboard/learning/${type}/${id}${tab ? `?tab=${tab}` : ''}`;
}

export const SIGNAL_DESCRIPTIONS: Record<string, { label: string; help: string }> = {
  execution_status: { label: 'Failures', help: 'Runs that errored count as negative examples.' },
  empty_output: { label: 'Empty results', help: 'Runs that produced nothing useful count as weak negatives.' },
  rerun_adjustments: { label: 'Reruns with notes', help: 'When someone re-ran a chain with adjustment notes, the original run is treated as corrected (negative) and the notes explain why.' },
  repeat_input: { label: 'Repeated requests', help: 'The same input sent again within an hour suggests the first answer was not good enough.' },
  explicit_feedback: { label: 'Feedback (thumbs, clicks, purchases)', help: 'Signals sent to the feedback API or clicked in the run details page. Strongest signal when present — never required.' },
  ai_judge: { label: 'AI quality check', help: 'The analyzer grades every sampled run (good / mixed / bad) and down-weights learnings from poor runs. Works with zero feedback.' },
  gcs_review_files: { label: 'Reviewer files in storage', help: 'Reads human review JSON files saved to your storage bucket under the prefix below.' },
  test_results: {
    label: 'Test results',
    help: 'Failing test cases (what was expected and why they failed) are analyzed first, with a few passing ones as good examples. Uses the latest results on the current default model, from tests run since the instructions last changed.',
  },
};

export const TIER_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  'positive feedback': 'success',
  'negative feedback / corrected': 'error',
  'failed / empty / repeated': 'warning',
  'no signal': 'default',
};

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.endsWith('Z') || value.includes('+') ? value : `${value}Z`);
  return d.toLocaleString();
}
